/**
 * Generate a PDF containing the heatmap image and AI analysis text.
 * Styled to resemble the PermoniQ Heatmaps web report UI.
 *
 * Uses the Onest font (latin-ext) for full Czech/Slovak diacritics support.
 * The heatmaps_logo_left.svg is rendered on the dark green header bar.
 */
import { jsPDF } from "jspdf";
import { getAiTypeDescription } from "../components/heatmaps/aiPurposeOptions";

// ---------------------------------------------------------------------------
// Brand colors
// ---------------------------------------------------------------------------
const BRAND = {
  headerBg: [18, 51, 9], // #123309 – dark green header
  green: [34, 224, 108], // #22e06c – primary green
  greenDark: [28, 180, 86], // darker green for accent
  black: [30, 30, 30], // near-black text
  gray: [100, 100, 100], // secondary text
  grayLight: [200, 200, 200], // borders
  white: [255, 255, 255],
  bgLight: [245, 248, 245], // light green tinted background
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Draw a filled rounded rectangle */
const roundedRect = (doc, x, y, w, h, r, fillColor) => {
  doc.setFillColor(...fillColor);
  doc.roundedRect(x, y, w, h, r, r, "F");
};

/** Ensure we don't go past page bottom; adds new page if needed */
const ensureSpace = (doc, y, needed, pageH, margin) => {
  if (y + needed > pageH - 15) {
    doc.addPage();
    return margin;
  }
  return y;
};

/**
 * Turn a structured AI analysis payload into sections for rendering.
 * Section headings now use proper Czech diacritics since we embed a custom font.
 */
const flattenPayload = (payload) => {
  if (!payload) return [];
  if (typeof payload === "string") {
    return [{ heading: null, items: [], text: payload }];
  }

  const sections = [];

  const addBlock = (obj) => {
    if (!obj || typeof obj !== "object") return;

    if (obj.typ) {
      sections.push({
        heading: getAiTypeDescription(obj.typ) || obj.typ,
        items: [],
        isType: true,
      });
    }

    if (obj.analyza && typeof obj.analyza === "object") {
      const a = obj.analyza;
      const items = [];
      if (a.logo) items.push({ label: "Logo", text: a.logo });
      if (a.claim) items.push({ label: "Claim", text: a.claim });
      if (a.cta) items.push({ label: "CTA", text: a.cta });
      if (a.podklad) items.push({ label: "Podklad", text: a.podklad });
      if (items.length) sections.push({ heading: "Analýza", items });
    }

    if (obj.doporuceni && typeof obj.doporuceni === "object") {
      const d = obj.doporuceni;
      const items = [];
      if (d.logo) items.push({ label: "Logo", text: d.logo });
      if (d.claim) items.push({ label: "Claim", text: d.claim });
      if (d.cta) items.push({ label: "CTA", text: d.cta });
      if (d.podklad) items.push({ label: "Podklad", text: d.podklad });
      if (items.length) sections.push({ heading: "Doporučení", items });
    }

    if (obj.hodnoceni) {
      sections.push({
        heading: "Celkové hodnocení",
        items: [],
        text: String(obj.hodnoceni),
      });
    }

    if (obj.permoniq_doporuceni) {
      sections.push({
        heading: "Doporučení PermoniQ",
        items: [],
        text: String(obj.permoniq_doporuceni),
      });
    }
  };

  if (Array.isArray(payload)) {
    payload.forEach(addBlock);
  } else {
    addBlock(payload);
  }

  return sections;
};

// ---------------------------------------------------------------------------
// Asset loading (cached singleton)
// ---------------------------------------------------------------------------

/** Convert an SVG URL to a PNG data URL via canvas */
const svgToPngDataUrl = (svgUrl, width, height) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => resolve(null);
    img.crossOrigin = "anonymous";
    img.src = svgUrl;
  });

/** Fetch a file URL and return raw base64 string (no data: prefix) */
const fetchAsBase64 = async (url) => {
  const resp = await fetch(url);
  const blob = await resp.blob();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(",")[1]);
    reader.readAsDataURL(blob);
  });
};

let _assetsPromise = null;

/**
 * Load and cache all external assets once:
 *   - SVG logo rendered to PNG via canvas
 *   - Onest Regular + Bold fonts as base64 for jsPDF embedding
 */
const loadAssets = () => {
  if (_assetsPromise) return _assetsPromise;
  _assetsPromise = (async () => {
    try {
      const [logoMod, faviconMod, fontRegMod, fontBoldMod] = await Promise.all([
        import("../img/logos/heatmaps_logo_left.svg"),
        import("../favicons/android-chrome-192x192.png"),
        import("../fonts/onest-v6-latin_latin-ext-regular.ttf"),
        import("../fonts/onest-v6-latin_latin-ext-700.ttf"),
      ]);

      const logoUrl = logoMod.default || logoMod;
      const faviconUrl = faviconMod.default || faviconMod;
      const fontRegUrl = fontRegMod.default || fontRegMod;
      const fontBoldUrl = fontBoldMod.default || fontBoldMod;

      // SVG viewBox is 801×262 → render at 2× for crisp PDF output
      const [logoPng, faviconDataUrl, fontRegular, fontBold] =
        await Promise.all([
          svgToPngDataUrl(logoUrl, 1602, 524),
          fetch(faviconUrl)
            .then((r) => r.blob())
            .then(
              (blob) =>
                new Promise((resolve) => {
                  const reader = new FileReader();
                  reader.onloadend = () => resolve(reader.result);
                  reader.readAsDataURL(blob);
                }),
            ),
          fetchAsBase64(fontRegUrl),
          fetchAsBase64(fontBoldUrl),
        ]);

      return { logoPng, faviconDataUrl, fontRegular, fontBold };
    } catch (e) {
      console.warn("Failed to load PDF assets", e);
      return {
        logoPng: null,
        faviconDataUrl: null,
        fontRegular: null,
        fontBold: null,
      };
    }
  })();
  return _assetsPromise;
};

// ---------------------------------------------------------------------------
// Register Onest font with a jsPDF instance
// ---------------------------------------------------------------------------
const registerFonts = (doc, fontRegular, fontBold) => {
  if (fontRegular) {
    doc.addFileToVFS("Onest-Regular.ttf", fontRegular);
    doc.addFont("Onest-Regular.ttf", "Onest", "normal");
  }
  if (fontBold) {
    doc.addFileToVFS("Onest-Bold.ttf", fontBold);
    doc.addFont("Onest-Bold.ttf", "Onest", "bold");
  }
};

/** Set font, falling back to Helvetica if Onest wasn't loaded */
const applyFont = (doc, style, hasOnest) => {
  doc.setFont(hasOnest ? "Onest" : "helvetica", style);
};

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

/**
 * @param {Object} options
 * @param {string} options.imageBase64      – raw base64 (or data:… prefixed)
 * @param {string} [options.imageName]      – displayed in the PDF
 * @param {*}      [options.analysisPayload] – structured AI analysis
 * @param {{ val:number, max:number }} [options.score]
 * @param {string} [options.fileName]       – download file name (no .pdf)
 * @param {boolean} [options.asBlob]        – return Blob instead of triggering download
 * @returns {Promise<Blob|void>}
 */
export const generateHeatmapPdf = async ({
  imageBase64,
  imageName = "heatmapa",
  analysisPayload,
  score,
  fileName,
  asBlob = false,
}) => {
  let b64 = imageBase64 || "";
  if (b64.startsWith("data:")) {
    b64 = b64.split(",")[1] || "";
  }

  // Pre-load all assets (cached after first call)
  const { logoPng, faviconDataUrl, fontRegular, fontBold } = await loadAssets();

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth(); // 210
  const pageH = doc.internal.pageSize.getHeight(); // 297
  const margin = 14;
  const contentW = pageW - margin * 2;
  let y = 0;

  // Register custom font for diacritics
  const hasOnest = !!(fontRegular && fontBold);
  registerFonts(doc, fontRegular, fontBold);

  // =========================================================================
  // DARK GREEN HEADER BAR  (#123309)
  // =========================================================================
  const headerH = 24;
  doc.setFillColor(...BRAND.headerBg);
  doc.rect(0, 0, pageW, headerH, "F");

  // SVG logo (white text + green Q) on the dark header
  if (logoPng) {
    try {
      // Original aspect ratio ~3.06:1  →  logoW / logoH = 3.06
      const logoH = 10;
      const logoW = logoH * (801 / 262); // ≈ 30.6 mm
      const logoY = (headerH - logoH) / 2;
      doc.addImage(logoPng, "PNG", margin, logoY, logoW, logoH);
    } catch (e) {
      // fallback: no logo
    }
  }

  doc.setTextColor(...BRAND.black);
  y = headerH + 10;

  // =========================================================================
  // IMAGE NAME / TITLE
  // =========================================================================
  applyFont(doc, "bold", hasOnest);
  doc.setFontSize(16);
  doc.text(imageName, margin, y);
  y += 9;

  // =========================================================================
  // HEATMAP IMAGE (with subtle border)
  // =========================================================================
  if (b64) {
    try {
      const imgProps = doc.getImageProperties(`data:image/png;base64,${b64}`);
      const ratio = imgProps.width / imgProps.height;
      let imgW = contentW;
      let imgH = contentW / ratio;
      const maxImgH = 120;
      if (imgH > maxImgH) {
        imgH = maxImgH;
        imgW = maxImgH * ratio;
      }
      const imgX = margin + (contentW - imgW) / 2;

      // Subtle border
      doc.setDrawColor(...BRAND.grayLight);
      doc.setLineWidth(0.3);
      doc.rect(imgX - 0.5, y - 0.5, imgW + 1, imgH + 1);

      doc.addImage(`data:image/png;base64,${b64}`, "PNG", imgX, y, imgW, imgH);
      y += imgH + 6;
    } catch (e) {
      console.warn("Failed to embed image into PDF", e);
    }
  }

  // =========================================================================
  // SCORE BADGE  (green pill, BLACK text)
  // =========================================================================
  if (score && typeof score.val === "number" && typeof score.max === "number") {
    y = ensureSpace(doc, y, 14, pageH, margin);
    const scoreText = `Skóre: ${score.val} / ${score.max}`;
    const badgeW = 50;
    const badgeH = 11;
    const badgeX = margin;
    roundedRect(doc, badgeX, y, badgeW, badgeH, 4, BRAND.green);
    applyFont(doc, "bold", hasOnest);
    doc.setFontSize(13);
    doc.setTextColor(...BRAND.black);
    doc.text(scoreText, badgeX + badgeW / 2, y + badgeH / 2 + 1.5, {
      align: "center",
    });
    y += badgeH + 8;
  }

  // =========================================================================
  // AI ANALYSIS SECTIONS
  // =========================================================================
  const sections = flattenPayload(analysisPayload);
  if (sections.length > 0) {
    // Green divider line
    doc.setDrawColor(...BRAND.green);
    doc.setLineWidth(0.6);
    doc.line(margin, y, pageW - margin, y);
    y += 10;

    for (const section of sections) {
      y = ensureSpace(doc, y, 22, pageH, margin);

      // ---- Section heading with green left accent bar ----
      if (section.heading) {
        const headingText = section.heading;

        if (section.isType) {
          // "Typ" heading – green background bar
          const barH = 10;
          roundedRect(doc, margin, y, contentW, barH, 2, BRAND.bgLight);
          // Green left accent
          doc.setFillColor(...BRAND.green);
          doc.rect(margin, y, 2.5, barH, "F");
          applyFont(doc, "bold", hasOnest);
          doc.setFontSize(13);
          doc.setTextColor(...BRAND.greenDark);
          doc.text(headingText, margin + 6, y + barH / 2 + 1.5);
          doc.setTextColor(...BRAND.black);
          y += barH + 5;
        } else {
          // Regular section heading with green left bar
          doc.setFillColor(...BRAND.green);
          doc.rect(margin, y, 2, 7, "F");
          applyFont(doc, "bold", hasOnest);
          doc.setFontSize(13);
          doc.setTextColor(...BRAND.black);
          doc.text(headingText, margin + 5, y + 5.5);
          y += 11;
        }
      }

      // ---- Bullet items (label: text) ----
      if (section.items && section.items.length > 0) {
        for (const item of section.items) {
          y = ensureSpace(doc, y, 10, pageH, margin);
          const label = item.label || "";
          const text = item.text || "";

          // PermoniQ favicon as bullet icon
          if (faviconDataUrl) {
            try {
              doc.addImage(faviconDataUrl, "PNG", margin + 0.5, y - 0.3, 4, 4);
            } catch (e) {
              doc.setFillColor(...BRAND.green);
              doc.circle(margin + 2.5, y + 1.8, 1, "F");
            }
          } else {
            doc.setFillColor(...BRAND.green);
            doc.circle(margin + 2.5, y + 1.8, 1, "F");
          }

          // Bold label
          applyFont(doc, "bold", hasOnest);
          doc.setFontSize(11);
          doc.text(`${label}:`, margin + 6, y + 3);
          const labelWidth = doc.getTextWidth(`${label}: `);

          // Normal text (wrapped)
          applyFont(doc, "normal", hasOnest);
          doc.setFontSize(11);
          const availW = contentW - 6 - labelWidth;
          const wrapped = doc.splitTextToSize(
            text,
            availW > 30 ? availW : contentW - 6,
          );

          if (wrapped.length === 1 && availW > 30) {
            doc.text(wrapped[0], margin + 6 + labelWidth, y + 3);
            y += 7;
          } else {
            const allWrapped = doc.splitTextToSize(text, contentW - 10);
            y += 6;
            for (const wl of allWrapped) {
              y = ensureSpace(doc, y, 6, pageH, margin);
              doc.text(wl, margin + 6, y + 3);
              y += 5.2;
            }
            y += 1.5;
          }
        }
        y += 3;
      }

      // ---- Plain text (e.g., hodnocení, doporučení PermoniQ) ----
      if (section.text) {
        applyFont(doc, "normal", hasOnest);
        doc.setFontSize(11);
        const allWrapped = doc.splitTextToSize(section.text, contentW - 4);
        for (const wl of allWrapped) {
          y = ensureSpace(doc, y, 6, pageH, margin);
          doc.text(wl, margin + 2, y + 3.5);
          y += 5.2;
        }
        y += 4;
      }
    }
  }

  // =========================================================================
  // FOOTER on every page
  // =========================================================================
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    // Green footer line
    doc.setDrawColor(...BRAND.green);
    doc.setLineWidth(0.4);
    doc.line(margin, pageH - 12, pageW - margin, pageH - 12);

    applyFont(doc, "normal", hasOnest);
    doc.setFontSize(7.5);
    doc.setTextColor(...BRAND.gray);
    doc.text("permoniq.com", margin, pageH - 8);
    doc.text(`${i} / ${totalPages}`, pageW - margin, pageH - 8, {
      align: "right",
    });

    // Small favicon in footer
    if (faviconDataUrl) {
      try {
        const footerIconSize = 4;
        doc.addImage(
          faviconDataUrl,
          "PNG",
          pageW / 2 - footerIconSize / 2,
          pageH - 11,
          footerIconSize,
          footerIconSize,
        );
      } catch (e) {
        // no-op
      }
    }

    doc.setTextColor(...BRAND.black);
  }

  // =========================================================================
  // Output
  // =========================================================================
  const safeName = (fileName || `heatmap_${imageName}`)
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_\-\.]/g, "");

  if (asBlob) {
    return doc.output("blob");
  }
  doc.save(`${safeName}.pdf`);
};

/**
 * Convenience: given analysisData item + AI payload, build a PDF for a single heatmap.
 *
 * @param {Object} item – analysisData item (has overlay_image_base64, heatmap_image_base64, image_id, etc.)
 * @param {*} aiPayload – structured analysis payload (object/array/string) or null
 * @param {{ val: number, max: number }|null} score
 */
export const downloadHeatmapAsPdf = async (item, aiPayload, score) => {
  const imageBase64 =
    item.overlay_image_base64 || item.heatmap_image_base64 || "";
  const imageName = item.image_id || item.name || "heatmapa";
  const baseName = imageName.split(".").slice(0, -1).join(".") || imageName;

  await generateHeatmapPdf({
    imageBase64,
    imageName,
    analysisPayload: aiPayload,
    score,
    fileName: `heatmap_${baseName}`,
  });
};

export default generateHeatmapPdf;
